import React from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import {
  X, FileText, CreditCard, FolderOpen, Bell, Shield, HelpCircle,
} from 'lucide-react';

/**
 * Banner di benvenuto mostrato in cima alla UserDashboard al primo accesso.
 * L'utente lo può dismettere e non vede più. Stato in localStorage (per utente).
 */
const WelcomeBanner = ({ userName, onDismiss }) => {
  const firstName = (userName || '').split(' ')[0] || 'Utente';

  const features = [
    { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Contratti', desc: 'I tuoi contratti di locazione, sempre aggiornati' },
    { icon: CreditCard, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Pagamenti', desc: 'Storico canoni e ricevute scaricabili' },
    { icon: FolderOpen, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Documenti', desc: 'Tutti gli allegati ai contratti in un click' },
    { icon: Bell, color: 'text-purple-600', bg: 'bg-purple-50', label: 'Scadenziario', desc: 'Promemoria su scadenze importanti' },
  ];

  return (
    <Card className="relative overflow-hidden border-blue-200 bg-gradient-to-br from-blue-50 via-white to-blue-50/50 mb-6" data-testid="welcome-banner">
      <button
        onClick={onDismiss}
        className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-slate-200/60 text-slate-500 hover:text-slate-700 transition-colors"
        aria-label="Chiudi banner di benvenuto"
        data-testid="welcome-banner-close"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="p-6 sm:p-8">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center text-lg font-bold shrink-0">
            {firstName.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-slate-900">
              Ciao {firstName}, benvenuto su Semplificapp! 👋
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Qui trovi tutto quello che serve per gestire la tua locazione in modo semplice e sicuro.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {features.map((f) => (
            <div
              key={f.label}
              className="flex gap-3 p-3 rounded-lg bg-white/70 border border-slate-200/60"
              data-testid={`welcome-feature-${f.label.toLowerCase()}`}
            >
              <div className={`w-10 h-10 rounded-lg ${f.bg} ${f.color} flex items-center justify-center shrink-0`}>
                <f.icon className="w-5 h-5" strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-sm text-slate-900">{f.label}</p>
                <p className="text-xs text-slate-500 leading-tight">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3 text-sm">
          <div className="flex items-center gap-1.5 text-slate-600">
            <Shield className="w-4 h-4 text-emerald-600" />
            <span>I tuoi dati sono protetti secondo GDPR</span>
          </div>
          <span className="text-slate-300">·</span>
          <a href="/faq" className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1" data-testid="welcome-faq-link">
            <HelpCircle className="w-4 h-4" />
            Domande frequenti
          </a>
          <span className="text-slate-300">·</span>
          <a href="/privacy" className="text-blue-600 hover:text-blue-800 hover:underline" data-testid="welcome-privacy-link">
            Informativa privacy
          </a>
          <div className="ml-auto">
            <Button
              size="sm"
              variant="default"
              onClick={onDismiss}
              data-testid="welcome-banner-confirm"
            >
              Inizia
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default WelcomeBanner;
